import { useState, useCallback, useEffect } from 'react';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import useCreateApiKeyMutation from './useCreateApiKeyMutation';
import useGetApiKeysQuery, { type ApiKey } from './useGetApiKeyQuery';
import useRevokeApiKeyMutation from './useRevokeApiKeyMutation';

interface ApiKeyState {
  apiKeys: ApiKey[];
  /** The one key whose full value is still on screen, straight after creating it. */
  displayKey: { id: number; key: string } | null;
  loading: boolean;
  error: string | null;
}

export const useApiKey = () => {
  const [state, setState] = useState<ApiKeyState>({
    apiKeys: [],
    displayKey: null,
    loading: true,
    error: null,
  });

  const { currentOrg } = useUserStateContext();
  const orgId = currentOrg?.id;

  const createApiKeyMutation = useCreateApiKeyMutation(orgId);
  const deleteApiKeyMutation = useRevokeApiKeyMutation(orgId);
  const fetchApiKeysQuery = useGetApiKeysQuery(orgId);

  // Handle fetch API keys state
  useEffect(() => {
    if (fetchApiKeysQuery.status === 'success' && fetchApiKeysQuery.data) {
      setState(prev => ({ ...prev, apiKeys: fetchApiKeysQuery.data }));
    } else if (fetchApiKeysQuery.status === 'error') {
      setState(prev => ({
        ...prev,
        error: fetchApiKeysQuery.error?.message ?? 'An error occurred'
      }));
    }
  }, [fetchApiKeysQuery.status, fetchApiKeysQuery.data, fetchApiKeysQuery.error]);

  // Handle create API key state. The list itself refreshes through
  // invalidateQueries — all this keeps is the full key, which the list never
  // carries and the server will not hand out again.
  useEffect(() => {
    if (createApiKeyMutation.status === 'success' && createApiKeyMutation.data) {
      setState(prev => ({
        ...prev,
        displayKey: {
          id: createApiKeyMutation.data.id,
          key: createApiKeyMutation.data.key,
        },
      }));
    } else if (createApiKeyMutation.status === 'error') {
      setState(prev => ({
        ...prev,
        error: createApiKeyMutation.error?.message ?? 'Failed to create API key'
      }));
    }
  }, [createApiKeyMutation.status, createApiKeyMutation.data, createApiKeyMutation.error]);

  // Handle delete API key state
  useEffect(() => {
    if (deleteApiKeyMutation.status === 'success') {
      // Only drop the banner if the key it is showing is the one just revoked —
      // revoking an old key should not hide a new key you have yet to copy.
      const revokedId = deleteApiKeyMutation.variables;
      setState(prev => ({
        ...prev,
        displayKey: prev.displayKey?.id === revokedId ? null : prev.displayKey,
      }));
    } else if (deleteApiKeyMutation.status === 'error') {
      setState(prev => ({
        ...prev,
        error: deleteApiKeyMutation.error?.message ?? 'Failed to delete API key'
      }));
    }
  }, [deleteApiKeyMutation.status, deleteApiKeyMutation.variables, deleteApiKeyMutation.error]);

  // Handle loading state
  useEffect(() => {

    setState(prev => ({
      ...prev,
      loading:
        fetchApiKeysQuery.isLoading ||
        createApiKeyMutation.isPending ||
        deleteApiKeyMutation.isPending
    }));
  }, [
    fetchApiKeysQuery.isLoading,
    createApiKeyMutation.isPending,
    deleteApiKeyMutation.isPending
  ]);



  const createApiKey = useCallback(async (description?: string) => {
    try {
      await createApiKeyMutation.mutateAsync(description);
    } catch {
      // Error handling is managed by the mutation
    }
  }, [createApiKeyMutation]);

  const deleteApiKey = useCallback(async (id: number) => {
    try {
      await deleteApiKeyMutation.mutateAsync(id);
    } catch {
      // Error handling is managed by the mutation
    }
  }, [deleteApiKeyMutation]);

  return {
    ...state,
    createApiKey,
    deleteApiKey,
  };
};
